import type { MfgMissingCostMaterial } from "./cogsTypes";

export type MissingMaterialSourceRow = {
  subject_kind: string;
  code: string;
  name: string;
  materials_unpriced_leaf_count?: number;
  materials_missing_costs?: MfgMissingCostMaterial[];
};

export type MissingMaterialLine = {
  material_id: string;
  material_code: string;
  name: string;
  unit: string;
  quantity: number;
  sku_count: number;
  sku_codes: string[];
};

export type UnnamedMissingSku = {
  code: string;
  name: string;
  unnamed_count: number;
};

function roundQty(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** One row per unpriced material, summed across saved product COGS summaries. */
export function aggregateMissingMaterials(rows: readonly MissingMaterialSourceRow[]): {
  materials: MissingMaterialLine[];
  unnamed: UnnamedMissingSku[];
} {
  const byMaterial = new Map<
    string,
    {
      material_id: string;
      material_code: string;
      name: string;
      unit: string;
      quantity: number;
      skus: Set<string>;
    }
  >();
  const unnamed: UnnamedMissingSku[] = [];

  for (const row of rows) {
    if (row.subject_kind !== "recipe") continue;
    const listed = row.materials_missing_costs ?? [];
    const unpriced = row.materials_unpriced_leaf_count ?? listed.length;
    if (unpriced > listed.length) {
      unnamed.push({
        code: row.code,
        name: row.name,
        unnamed_count: unpriced - listed.length,
      });
    }
    for (const material of listed) {
      const key = material.material_id || material.material_code || material.name;
      if (!key) continue;
      let agg = byMaterial.get(key);
      if (!agg) {
        agg = {
          material_id: material.material_id,
          material_code: material.material_code,
          name: material.name,
          unit: material.unit,
          quantity: 0,
          skus: new Set(),
        };
        byMaterial.set(key, agg);
      }
      if (Number.isFinite(material.quantity)) agg.quantity += material.quantity;
      if (row.code) agg.skus.add(row.code);
    }
  }

  const materials = [...byMaterial.values()].map((row) => ({
    material_id: row.material_id,
    material_code: row.material_code,
    name: row.name,
    unit: row.unit,
    quantity: roundQty(row.quantity),
    sku_count: row.skus.size,
    sku_codes: [...row.skus].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
  }));
  materials.sort(
    (a, b) =>
      b.sku_count - a.sku_count ||
      a.material_code.localeCompare(b.material_code, undefined, { sensitivity: "base" }) ||
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
  unnamed.sort((a, b) => a.code.localeCompare(b.code, undefined, { sensitivity: "base" }));
  return { materials, unnamed };
}
